import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import StatisticsService from "../../../services/statistics.service";
import StatDescriptionTooltip from "./StatDescriptionTooltip";
import StatAsyncContent from "./StatAsyncContent";
import StatLineChart from "./StatLineChart";
import StatSelect from "./StatSelect";
import { getProductStroke, TOTAL_STROKE } from "./statChartTheme";

const MONTH_OPTIONS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function buildDayChartData(totalsByDay, productSeriesNested, year, month) {
  const dayCount = Math.max(
    daysInMonth(year, month),
    ...(totalsByDay ?? []).map((item) => item.day_number || 0),
    ...(productSeriesNested ?? []).flat().map((item) => item.day_number || 0)
  );

  const byDay = {};
  for (let i = 1; i <= dayCount; i++) {
    const date = `${year}-${String(month).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
    byDay[i] = { day: i, date, total: 0 };
  }

  for (const item of totalsByDay ?? []) {
    if (!byDay[item.day_number]) continue;
    byDay[item.day_number].total = item.total_users_with_active_membership ?? 0;
    if (item.date) byDay[item.day_number].date = item.date;
  }

  const seriesNames = [];
  const seen = new Set();

  for (const point of (productSeriesNested ?? []).flat()) {
    const name = point.product_name || "Sin producto";
    if (!seen.has(name)) {
      seen.add(name);
      seriesNames.push(name);
    }
    const row = byDay[point.day_number];
    if (!row) continue;
    row[name] = (row[name] ?? 0) + (point.total_users ?? 0);
    if (point.date) row.date = point.date;
  }

  for (const row of Object.values(byDay)) {
    for (const name of seriesNames) {
      if (row[name] == null) row[name] = 0;
    }
  }

  return {
    data: Array.from({ length: dayCount }, (_, i) => byDay[i + 1]),
    seriesNames,
  };
}

export default function ActiveMembershipsByDayStat() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [lines, setLines] = useState(null);
  const [description, setDescription] = useState(null);
  const [error, setError] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);

  useEffect(() => {
    StatisticsService.getMembershipManagedYears().then(({ data, error }) => {
      setLoadingYears(false);
      if (error) {
        setError(error);
        return;
      }
      const yearList = (data ?? []).map((item) => item.year);
      setYears(yearList);
      if (yearList.length > 0) setSelectedYear(yearList[0]);
    });
  }, []);

  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    setData(null);
    setLines(null);
    setError(null);

    Promise.all([
      StatisticsService.getActiveMembershipsByDay(selectedYear, selectedMonth),
      StatisticsService.getActiveMembershipsByProduct(selectedYear, selectedMonth),
    ]).then(([totalsResult, productsResult]) => {
      if (totalsResult.error) {
        setError(totalsResult.error);
        return;
      }
      if (productsResult.error) {
        setError(productsResult.error);
        return;
      }

      const { data: chartData, seriesNames } = buildDayChartData(
        totalsResult.data,
        productsResult.data,
        selectedYear,
        selectedMonth
      );

      setData(chartData);
      setLines([
        { dataKey: "total", name: "Total", stroke: TOTAL_STROKE },
        ...seriesNames.map((name, index) => ({
          dataKey: name,
          name,
          stroke: getProductStroke(index, TOTAL_STROKE),
        })),
      ]);
      setDescription(productsResult.description ?? totalsResult.description);
    });
  }, [selectedYear, selectedMonth]);

  return (
    <Card sx={{ width: "100%", overflow: "visible" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h6">Membresías activas por día</Typography>
            <StatDescriptionTooltip description={description} />
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatSelect
              label="Año"
              labelId="membership-day-year-label"
              value={selectedYear ?? ""}
              onChange={setSelectedYear}
              options={years}
              disabled={loadingYears || years.length === 0}
            />
            <StatSelect
              label="Mes"
              labelId="membership-day-month-label"
              value={selectedMonth}
              onChange={setSelectedMonth}
              options={MONTH_OPTIONS}
              minWidth={140}
              disabled={loadingYears || years.length === 0}
            />
          </Box>
        </Box>

        <StatAsyncContent error={error} loading={data === null}>
          <StatLineChart
            data={data}
            xKey="day"
            lines={lines}
            dotRadius={3}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ""}
          />
        </StatAsyncContent>
      </CardContent>
    </Card>
  );
}
